import React, { useEffect, useRef, useState } from 'react'
import { Link, generatePath } from 'react-router-dom'
import Dialog, { DIALOG_ASIDE_FOOTER } from 'components/dialog'
import ActionDialog from 'components/dialog/action'
import Button from 'components/button'
import Empty from 'components/empty'
import Field from 'components/field'
import Select from 'components/select'
import Toggle from 'components/toggle'
import Tooltip from 'components/tooltip'
import Spinner from 'components/spinner'
import SelectMenu from 'components/select/components/menu'
import { MenuItem, MenuLabel, MenuSearch } from 'components/menu/items'
import HoverCard from 'components/hover-card'
import { PanelAction, PanelAdd, PanelPill, PanelRule, PanelRules, PanelSegmentSelect } from 'components/menu/panel'
import { BLUE_BUTTON } from 'components/save-buttons'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import {
  ALLOWED_VIEW_TYPES_ON_SELECT_OPTIONS,
  BLOCK_SELECT_OPTION_TYPES,
  FilterVariables
} from 'components/spreadsheet/constants/const'
import { saveCurrentView } from 'components/spreadsheet/contexts/data/actions'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import {
  Arrow,
  Dependency,
  Expand,
  ExternalLink,
  Grid,
  Join,
  Lock,
  Pencil,
  Plus,
  Search,
  Spreadsheet,
  Tick,
  Triangle,
  Warning
} from 'components/icons'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import { getProjectTables } from 'helpers/utils'
import api from 'helpers/api'
import { useViewer } from 'hooks/useViewer'
import { TABLE } from 'routes'
import {
  IContextMenuState,
  ICreateTableJoin,
  IMapJoinColumn,
  ITable,
  ITableColumn,
  ITableJoin,
  ITableViewColumn,
  ITableViewWithColumns
} from 'types'

// Lookups bring columns from other tables into this one: each row takes the values of the row in the other
// table that matches it. The tables they come from are listed in a sidebar; the one picked shows its source,
// the rules that match the rows, what a row takes when several match, and the other table's columns, each with
// a switch to look it up or not. A select that takes its options from a table is a connection to it already,
// so that table is listed too, its match set, with nothing looked up until a column is switched on. Beside
// them, a preview of the column values each row would take, by the value it's matched on. Nothing is saved until Save, as it changes the
// table's columns.

/**
 * Opens a table's lookups, with the one that fills a column (its id) in view: a lookup column's header does
 * this. Named by table, as a document can show several, each with its own toolbar listening.
 */
export const OPEN_LOOKUPS_EVENT = 'morta:open-lookups'
export interface OpenLookupsDetail {
  tableId: string
  joinId?: string
}
export const openLookups = (detail: OpenLookupsDetail): void => {
  window.dispatchEvent(new CustomEvent<OpenLookupsDetail>(OPEN_LOOKUPS_EVENT, { detail }))
}

// The source rows the preview reads, to show what each value matched takes: enough to find the rows that share
// a value, never all of a large table.
const PREVIEW_ROWS = 500
// The imported columns listed before the rest are under View all.
const LISTED_IMPORTED = 5
const MULTI_KINDS = ['multiselect', 'multilink']
// The source, as Edit column's Source column shows it: on light blue, its table in blue.
const SOURCE_ROW =
  'flex h-9 w-full items-center justify-start gap-1.5 rounded-lg bg-really-light-blue px-3 text-[13px] font-medium text-body [&_svg]:size-3.5 [&_svg]:shrink-0'

/** A source's table in blue, then its view. */
const sourceName = (draft: LookupDraft) => (
  <>
    <span className="flex text-blue">
      <Grid />
    </span>
    <span className="min-w-0 truncate text-blue">{draft.tableName}</span>
    {draft.viewName && (
      <>
        <span className="shrink-0 text-faint">›</span>
        <span className="max-w-[35%] shrink-0 truncate">{draft.viewName}</span>
      </>
    )}
  </>
)

/** A lookup as it's being set up: a saved one (with its id), or one not yet saved. */
export interface LookupDraft {
  /** Its id, or "new-<view id>" before it's saved. */
  key: string
  joinId?: string
  tableId: string
  tableName: string
  viewId: string
  viewName: string
  joinColumns: IMapJoinColumn[]
  /** The source's columns to look up. */
  dataColumns: string[]
  isOneToMany: boolean
  /** Listed because that table looks up from this one: the way back, its dependency reversed. */
  looksUpFromHere?: boolean
}

const draftOf = (join: ITableJoin): LookupDraft => ({
  key: join.publicId,
  joinId: join.publicId,
  tableId: join.joinTableId,
  tableName: join.joinTableName,
  viewId: join.joinViewId,
  viewName: join.joinViewName,
  joinColumns: join.joinColumns.map(({ sourceColumnId, targetColumnId }) => ({ sourceColumnId, targetColumnId })),
  dataColumns: join.dataColumns.map((column) => column.sourceColumnId),
  isOneToMany: join.isOneToMany
})

/** A select of this table whose options come from a view of another, as a match on that view. */
const linkedSourceColumn = (column: ITableColumn | undefined, viewId: string): string | undefined => {
  const source = column?.kindOptions?.tableOptions
  return source?.viewId === viewId && source.columnId ? source.columnId : undefined
}

/**
 * Each table this one's selects take their options from, with no lookup on that view yet: listed with the
 * select's match, so a column can be looked up from it with a switch.
 */
export const connectionDrafts = (columns: ITableColumn[], drafts: LookupDraft[], tables: ITable[]): LookupDraft[] =>
  columns.reduce<LookupDraft[]>((connections, column) => {
    const source = column.kindOptions?.tableOptions
    if (!source?.tableId || !source.viewId || !source.columnId || column.isJoined) return connections
    if ([...drafts, ...connections].some((draft) => draft.viewId === source.viewId)) return connections
    const table = tables.find((candidate) => candidate.publicId === source.tableId)
    if (!table) return connections
    return [
      ...connections,
      {
        key: `new-${source.viewId}`,
        tableId: source.tableId,
        tableName: table.name,
        viewId: source.viewId,
        viewName: '',
        joinColumns: [{ sourceColumnId: source.columnId, targetColumnId: column.publicId }],
        dataColumns: [],
        isOneToMany: false
      }
    ]
  }, [])

/**
 * Each table that looks up from this one, as a source to look up from in return: its default view, the
 * dependency it uses turned round (its column matched to this table's), taking all its matching rows as a list,
 * as one row here usually matches several there; nothing imported until a column is switched on. A dependency
 * on a column a lookup fills on either side can't be turned round, so it's left out.
 */
export const reverseDrafts = (
  thisTable: { publicId: string; columns: ITableColumn[] },
  readers: Array<{ tableId: string; tableName: string; viewId: string; joins: ITableJoin[] }>,
  drafts: LookupDraft[]
): LookupDraft[] =>
  readers.reduce<LookupDraft[]>((reversed, reader) => {
    if ([...drafts, ...reversed].some((draft) => draft.tableId === reader.tableId)) return reversed
    const join = reader.joins.find((item) => item.joinTableId === thisTable.publicId)
    if (!join) return reversed
    const joinColumns = join.joinColumns
      .filter((rule) => {
        const here = thisTable.columns.find((column) => column.publicId === rule.sourceColumnId)
        return here && !here.isJoined
      })
      .map((rule) => ({ sourceColumnId: rule.targetColumnId, targetColumnId: rule.sourceColumnId }))
    if (joinColumns.length === 0) return reversed
    return [
      ...reversed,
      {
        key: `new-${reader.viewId}`,
        tableId: reader.tableId,
        tableName: reader.tableName,
        viewId: reader.viewId,
        viewName: '',
        joinColumns,
        dataColumns: [],
        isOneToMany: true,
        looksUpFromHere: true
      }
    ]
  }, [])

// A link to a table's view, as the backend gives it: "/hub/<hub>/table/<table>/view/<view>".
const tableViewOf = (link: string): { tableId: string; viewId: string } | undefined => {
  const match = link.match(/table\/([^/]+)\/view\/([^/?#]+)/)
  return match ? { tableId: match[1], viewId: match[2] } : undefined
}

const sameList = (a: string[], b: string[]): boolean => a.length === b.length && a.every((value) => b.includes(value))

const draftChanged = (draft: LookupDraft, saved: LookupDraft | undefined): boolean => {
  if (!saved) return draft.dataColumns.length > 0
  return (
    draft.viewId !== saved.viewId ||
    draft.isOneToMany !== saved.isOneToMany ||
    !sameList(draft.dataColumns, saved.dataColumns) ||
    JSON.stringify(draft.joinColumns) !== JSON.stringify(saved.joinColumns)
  )
}

/** "1 column", "3 columns". */
const columnCount = (count: number): string => `${count} ${count === 1 ? 'column' : 'columns'}`

/** A cell's value as the preview shows it: lists joined, nothing for an empty cell. */
export const previewValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(previewValue).filter(Boolean).join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') {
    const named = value as { name?: unknown; label?: unknown; value?: unknown }
    return previewValue(named.name ?? named.label ?? named.value ?? '')
  }
  return String(value)
}

/** A row's value in a column it's matched on, as the values it can match: each of a multi-select's. */
export const matchValues = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value]).map(previewValue).filter((item) => item !== '')

/**
 * How a dependency compares its two sides, as the lookup does: a list on both sides matches the same list (the
 * same values in any order when every matching row is taken, in the same order when the first is); otherwise
 * the two share a value, so a value here matches a source list holding it and the other way round.
 */
export type KeyComparison = 'shared' | 'sameSet' | 'sameList'

/** Each dependency's comparison, by the kinds of its two columns and whether every matching row is taken. */
export const keyComparisons = (
  rules: Array<{ source: { kind: string }; own: { kind: string } }>,
  takesAll: boolean
): KeyComparison[] =>
  rules.map(({ source, own }) => {
    if (!MULTI_KINDS.includes(source.kind) || !MULTI_KINDS.includes(own.kind)) return 'shared'
    return takesAll ? 'sameSet' : 'sameList'
  })

/**
 * Whether a source row, by its values in the dependencies' source columns, matches a row here, by its values in
 * theirs (each side as `matchValues` gives it): every dependency holds, compared as `comparisons` says (by a
 * shared value where it says nothing). An empty side matches nothing, not even another empty.
 */
export const keysMatch = (
  sourceValues: string[][],
  rowValues: string[][],
  comparisons: KeyComparison[] = []
): boolean =>
  sourceValues.every((values, index) => {
    const row = rowValues[index] ?? []
    if (values.length === 0) return false
    if (comparisons[index] === 'sameList')
      return values.length === row.length && values.every((value, at) => value === row[at])
    if (comparisons[index] === 'sameSet')
      return values.length === row.length && values.every((value) => row.includes(value))
    return values.some((value) => row.includes(value))
  })

// A table named in a sentence or a heading: its icon and name. A source is in blue, as Edit column's source is,
// which sets it apart from this table.
export const TableName: React.FC<{ name: string; current?: boolean; className?: string }> = ({
  name,
  current,
  className
}) => (
  <span className={`whitespace-nowrap ${current ? 'text-body' : 'text-blue'} ${className ?? ''}`}>
    <span className="mr-1 inline-flex align-[-2px] [&_svg]:size-3.5">
      <Grid />
    </span>
    <span className="font-medium">{name}</span>
  </span>
)

// A column named in a sentence: its type icon, then its name.
export const ColumnName: React.FC<{ column?: { kind: ITableColumn['kind']; name: string } }> = ({ column }) => (
  <span className="whitespace-nowrap">
    {column && (
      <span className="mr-1 inline-flex align-[-2px] text-muted [&_svg]:size-3.5">
        <IconSelector kind={column.kind} />
      </span>
    )}
    <span className="font-medium text-body">{column?.name ?? 'a column'}</span>
  </span>
)

// A column named in a rule but not picked there. Medium, its own width up to 160px (see Edit column).
const STATIC_FIELD_SEGMENT =
  'flex h-full min-w-0 max-w-[160px] shrink-0 items-center gap-1 px-1.5 pl-1 text-[13px] font-medium whitespace-nowrap text-body [&_svg]:size-3.5 [&_svg]:shrink-0 select-none'

export interface TableLookupsDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  /** The lookup to show first: the one a column header was pressed on. */
  focusJoinId?: string
}

const LookupsDialog: React.FC<TableLookupsDialogProps> = ({ open, setOpen, focusJoinId }) => {
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const table = spreadsheetData.tableDetails
  // This table's columns that a rule can match on: not one a lookup or an integration fills, nor a kind that
  // can't be compared. In the view's order, as the grid shows them, then any the view hides.
  const viewOrder = spreadsheetData.viewDetails.columns.map((column) => column.publicId)
  const ownColumns = table.columns
    .filter((column) => !column.isJoined && !column.isSynced && !BLOCK_SELECT_OPTION_TYPES.includes(column.kind))
    .sort((a, b) => {
      const at = viewOrder.indexOf(a.publicId)
      const bt = viewOrder.indexOf(b.publicId)
      return (at < 0 ? Infinity : at) - (bt < 0 ? Infinity : bt)
    })

  const [tables, setTables] = useState<ITable[] | undefined>(undefined)
  const [saved, setSaved] = useState<LookupDraft[] | undefined>(undefined)
  const [drafts, setDrafts] = useState<LookupDraft[]>([])
  // The source views as this user can read them, with their columns; null for one they can't open.
  const [sourceViews, setSourceViews] = useState<Record<string, ITableViewWithColumns | null | undefined>>({})
  // The views a source table offers, for changing a lookup's view.
  const [tableViews, setTableViews] = useState<Record<string, ITableViewWithColumns[] | null>>({})
  // Each source view's first rows: undefined while they load, null when they can't be read.
  const [previews, setPreviews] = useState<
    Record<string, { rows: Array<Record<string, unknown>>; total: number } | null | undefined>
  >({})
  // This table's first rows, in none of its views, for the preview to say which values land on them.
  const [ownRows, setOwnRows] = useState<{ rows: Array<Record<string, unknown>>; total: number } | null | undefined>(
    undefined
  )
  const [activeKey, setActiveKey] = useState<string | undefined>(focusJoinId)
  // The source being changed in its own small dialog: the table and view picked there, until Save.
  // The preview's search, behind an icon until wanted, for the source picked.
  const [previewSearch, setPreviewSearch] = useState('')
  const [previewSearchOpen, setPreviewSearchOpen] = useState(false)
  // The preview at full width, in a dialog of its own.
  const [previewExpanded, setPreviewExpanded] = useState(false)
  const previewSearchRef = useRef<HTMLInputElement>(null)
  // The dialog of all the open source's columns, and its search.
  const [columnsOpen, setColumnsOpen] = useState(false)
  // The columns imported when it opened: they're listed first, and stay where they are as switches flip.
  const [columnsOpenedWith, setColumnsOpenedWith] = useState<string[]>([])
  const [columnSearch, setColumnSearch] = useState('')
  const [sourcePicker, setSourcePicker] = useState<{ key: string; tableId: string; viewId: string } | null>(null)
  const [tablePicker, setTablePicker] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [saving, setSaving] = useState(false)
  const [viewSavePending, setViewSavePending] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  const savedByKey = (key: string): LookupDraft | undefined => saved?.find((draft) => draft.key === key)
  const changed = drafts.filter((draft) => draftChanged(draft, savedByKey(draft.key)))
  const hasChanges = changed.length > 0

  // The lookups and the hub's tables, once per opening.
  const loadLookups = async (): Promise<void> => {
    const [projectTables, joins] = await Promise.all([
      getProjectTables(project.publicId, false),
      api.getTableJoins(table.publicId).then((response) => response.data)
    ])
    const savedDrafts = joins.map(draftOf).sort((a, b) => a.tableName.localeCompare(b.tableName))
    setTables(projectTables)
    setSaved(savedDrafts)
    const listed = [...savedDrafts, ...connectionDrafts(table.columns, savedDrafts, projectTables)]
    setDrafts(listed)
    // Then the tables that look up from this one, as sources the other way: listed once they're known, so the
    // dialog doesn't wait on them, and left out if they can't be read.
    loadReaders(listed, projectTables).catch(() => undefined)
  }
  const loadReaders = async (listed: LookupDraft[], projectTables: ITable[]): Promise<void> => {
    const used = (await api.getWhereTableUsed(table.publicId)).data
    const readers = (
      await Promise.all(
        used
          .filter((item) => item.type === 'sourceJoin')
          .map((item) => tableViewOf(item.link))
          .filter((found): found is { tableId: string; viewId: string } => !!found && found.tableId !== table.publicId)
          .map(async ({ tableId, viewId }) => {
            const name = projectTables.find((candidate) => candidate.publicId === tableId)?.name
            if (!name) return undefined
            const joins = await api
              .getTableJoins(tableId)
              .then((response) => response.data)
              .catch(() => [])
            return { tableId, tableName: name, viewId, joins }
          })
      )
    ).filter(
      (reader): reader is { tableId: string; tableName: string; viewId: string; joins: ITableJoin[] } => !!reader
    )
    const reversed = reverseDrafts(table, readers, listed)
    if (reversed.length > 0) setDrafts((current) => [...current, ...reverseDrafts(table, readers, current)])
  }
  useEffect(() => {
    if (!open || !project.publicId) return
    loadLookups().catch((error) => displayErrorMessage(error))
  }, [open, project.publicId])

  // The first group is open in the preview until another is picked.
  const activeDraft = drafts.find((draft) => draft.key === activeKey) ?? drafts[0]

  // Another source starts on its whole preview; the search, opened, is ready to type in.
  useEffect(() => {
    setPreviewSearch('')
    setPreviewSearchOpen(false)
  }, [activeDraft?.key])
  useEffect(() => {
    if (previewSearchOpen || previewExpanded) previewSearchRef.current?.focus()
  }, [previewSearchOpen, previewExpanded])

  // Each group's source view, with its columns, once.
  const viewIds = drafts.map((draft) => draft.viewId).join()
  useEffect(() => {
    drafts.forEach((draft) => {
      if (draft.viewId in sourceViews) return
      setSourceViews((views) => ({ ...views, [draft.viewId]: undefined }))
      api
        .getTableView(draft.viewId, { ignoreCachedOptions: true })
        .then((response) => {
          setSourceViews((views) => ({ ...views, [draft.viewId]: response.data }))
          // A connection's view is named once it's known.
          setDrafts((current) =>
            current.map((item) =>
              item.viewId === draft.viewId && !item.viewName ? { ...item, viewName: response.data.name } : item
            )
          )
        })
        .catch(() => setSourceViews((views) => ({ ...views, [draft.viewId]: null })))
    })
  }, [viewIds])

  // The open group's first rows, for the preview.
  const previewViewId = activeDraft?.viewId
  useEffect(() => {
    if (!previewViewId || previewViewId in previews) return
    setPreviews((current) => ({ ...current, [previewViewId]: undefined }))
    api
      .getTableViewRowsPage(previewViewId, PREVIEW_ROWS)
      .then((response) =>
        setPreviews((current) => ({
          ...current,
          [previewViewId]: {
            rows: response.data.map((row) => row.rowData),
            total: response.metadata?.total ?? response.data.length
          }
        }))
      )
      .catch(() => setPreviews((current) => ({ ...current, [previewViewId]: null })))
  }, [previewViewId])

  // This table's rows, once per opening.
  useEffect(() => {
    if (!open) return
    setOwnRows(undefined)
    api
      .getTableRowsPage(table.publicId, PREVIEW_ROWS)
      .then((response) =>
        setOwnRows({
          rows: response.data.map((row) => row.rowData),
          total: response.metadata?.total ?? response.data.length
        })
      )
      .catch(() => setOwnRows(null))
  }, [open, table.publicId])

  // Opened on a column's lookup: that one.
  useEffect(() => {
    if (focusJoinId && saved) setActiveKey(focusJoinId)
  }, [focusJoinId, saved])

  const updateDraft = (key: string, change: (draft: LookupDraft) => LookupDraft): void =>
    setDrafts((current) => current.map((draft) => (draft.key === key ? change(draft) : draft)))

  const close = (): void => {
    if (hasChanges && !saving) setConfirmDiscard(true)
    else setOpen(false)
  }

  // ——— Changes to a group ———————————————————————————————————————————————————————————————

  const toggleColumn = (draft: LookupDraft, columnId: string): void =>
    updateDraft(draft.key, (item) => ({
      ...item,
      dataColumns: item.dataColumns.includes(columnId)
        ? item.dataColumns.filter((id) => id !== columnId)
        : [...item.dataColumns, columnId]
    }))

  // A rule matching a plain column here to a list in the source can only match every row it's in.
  const forcesOneToMany = (draft: LookupDraft): boolean =>
    draft.joinColumns.some((rule) => {
      const target = table.columns.find((column) => column.publicId === rule.targetColumnId)
      const source = sourceViews[draft.viewId]?.columns.find((column) => column.publicId === rule.sourceColumnId)
      return !!target && !!source && !MULTI_KINDS.includes(target.kind) && MULTI_KINDS.includes(source.kind)
    })
  // Whether a lookup takes every matching row: as chosen, or because a dependency leaves it no other way.
  const takesAll = (draft: LookupDraft): boolean => draft.isOneToMany || forcesOneToMany(draft)

  const setRules = (draft: LookupDraft, joinColumns: IMapJoinColumn[]): void =>
    updateDraft(draft.key, (item) => {
      const next = { ...item, joinColumns }
      return forcesOneToMany(next) ? { ...next, isOneToMany: true } : next
    })

  // A select linked to this view matches by its own source column, so that one's set rather than picked.
  const addRule = (draft: LookupDraft, targetColumnId: string): void => {
    const target = table.columns.find((column) => column.publicId === targetColumnId)
    setRules(draft, [
      ...draft.joinColumns,
      { targetColumnId, sourceColumnId: linkedSourceColumn(target, draft.viewId) ?? '' }
    ])
  }

  const setRuleTarget = (draft: LookupDraft, index: number, targetColumnId: string): void => {
    const target = table.columns.find((column) => column.publicId === targetColumnId)
    const implied = linkedSourceColumn(target, draft.viewId)
    setRules(
      draft,
      draft.joinColumns.map((rule, position) =>
        position === index
          ? {
              targetColumnId,
              sourceColumnId:
                implied ??
                (linkedSourceColumn(
                  table.columns.find((column) => column.publicId === rule.targetColumnId),
                  draft.viewId
                )
                  ? ''
                  : rule.sourceColumnId)
            }
          : rule
      )
    )
  }

  const setRuleSource = (draft: LookupDraft, index: number, sourceColumnId: string): void =>
    setRules(
      draft,
      draft.joinColumns.map((rule, position) => (position === index ? { ...rule, sourceColumnId } : rule))
    )

  const removeRule = (draft: LookupDraft, index: number): void =>
    setRules(
      draft,
      draft.joinColumns.filter((_, position) => position !== index)
    )

  // The views a source table offers (Spreadsheet views), fetched once per table.
  const loadTableViews = (tableId: string): Promise<ITableViewWithColumns[]> => {
    const known = tableViews[tableId]
    if (known) return Promise.resolve(known)
    return api.getTableViews(tableId, { ignoreColumns: true }).then((response) => {
      const views = response.data.filter((view) => ALLOWED_VIEW_TYPES_ON_SELECT_OPTIONS.includes(view.type))
      setTableViews((current) => ({ ...current, [tableId]: views }))
      return views
    })
  }

  // A table picked to look up from: its default view, matched already when a select here takes its options
  // from that view. A table with a group already opens that group.
  const lookUpTable = async (tableId: string): Promise<void> => {
    const existing = drafts.find((draft) => draft.tableId === tableId)
    if (existing) {
      setActiveKey(existing.key)
      return
    }
    const picked = tables?.find((candidate) => candidate.publicId === tableId)
    if (!picked) return
    // Its default view, or its first spreadsheet view, of those that can be a lookup's source.
    const addGroup = (views: ITableViewWithColumns[]): void => {
      const eligible = views.filter((candidate) => !viewUnavailable(candidate))
      const view = eligible.find((candidate) => candidate.publicId === picked.defaultViewId) ?? eligible[0]
      if (!view) {
        setSnackbarMessage({
          status: 'error',
          message:
            views.length > 0
              ? `Every view of ${picked.name} has filters that change per person, so none can be a lookup’s source.`
              : `${picked.name} has no spreadsheet view to look up from.`
        })
        return
      }
      const linked = ownColumns.find((column) => linkedSourceColumn(column, view.publicId))
      // Matched already through a linked select, or left to be set.
      const joinColumns = linked
        ? [{ targetColumnId: linked.publicId, sourceColumnId: linkedSourceColumn(linked, view.publicId)! }]
        : []
      const draft: LookupDraft = {
        key: `new-${view.publicId}`,
        tableId,
        tableName: picked.name,
        viewId: view.publicId,
        viewName: view.name,
        joinColumns,
        dataColumns: [],
        isOneToMany: false
      }
      setDrafts((current) => [...current, draft])
      setActiveKey(draft.key)
    }
    await loadTableViews(tableId).then(addGroup).catch(displayErrorMessage)
  }

  // ——— Saving ————————————————————————————————————————————————————————————————————————————

  // What Save would leave a group missing, if anything: a rule without its column, or columns with nothing
  // to match on.
  const problemOf = (draft: LookupDraft): string | undefined => {
    if (!draftChanged(draft, savedByKey(draft.key)) || draft.dataColumns.length === 0) return undefined
    if (draft.joinColumns.length === 0) return `Add a dependency for ${draft.tableName} to save.`
    if (draft.joinColumns.some((rule) => !rule.sourceColumnId || !rule.targetColumnId))
      return `Choose both columns of each dependency for ${draft.tableName} to save.`
    return undefined
  }
  const problem = drafts.map(problemOf).find(Boolean)

  const added = changed.reduce(
    (sum, draft) => sum + draft.dataColumns.filter((id) => !savedByKey(draft.key)?.dataColumns.includes(id)).length,
    0
  )
  const removed = changed.reduce(
    (sum, draft) =>
      sum + (savedByKey(draft.key)?.dataColumns ?? []).filter((id) => !draft.dataColumns.includes(id)).length,
    0
  )
  // "Adds 2 columns, removes 1 column", or "Removes 1 column".
  const summaryParts = [added > 0 && `adds ${columnCount(added)}`, removed > 0 && `removes ${columnCount(removed)}`]
    .filter(Boolean)
    .join(', ')
  const changeSummary = summaryParts.charAt(0).toUpperCase() + summaryParts.slice(1)

  const requestOf = (draft: LookupDraft): ICreateTableJoin => ({
    joinViewId: draft.viewId,
    joinColumns: draft.joinColumns,
    dataColumns: draft.dataColumns,
    isOneToMany: takesAll(draft)
  })

  // Each changed group in turn: a new one created, a saved one updated, or removed once nothing is looked up
  // from it. The table's columns and the view follow, as they did after a lookup was saved before.
  const saveDraft = (draft: LookupDraft) => {
    if (!draft.joinId) return api.createTableJoin(table.publicId, project.publicId, requestOf(draft))
    if (draft.dataColumns.length === 0) return api.deleteTableJoin(table.publicId, draft.joinId, project.publicId)
    return api.updateTableJoin(table.publicId, draft.joinId, project.publicId, requestOf(draft))
  }

  const handleSave = (): void => {
    if (saving || problem) return
    setSaving(true)
    changed
      .reduce<Promise<void>>(
        (previous, draft) =>
          previous
            .then(() => saveDraft(draft))
            .then((response) => setSpreadsheetData({ type: 'UPDATE_TABLE_JOINS', table: response.data as ITable })),
        Promise.resolve()
      )
      // The view is saved once the table's new columns are in it (next render), so they're kept shown.
      .then(() => setViewSavePending(true))
      .catch((error) => {
        displayErrorMessage(error)
        setSaving(false)
        // What did save is shown as saved, and what didn't stays to try again.
        return loadLookups()
      })
      .catch(() => undefined)
  }

  useEffect(() => {
    if (!viewSavePending) return
    setViewSavePending(false)
    saveCurrentView(
      project.publicId,
      spreadsheetData,
      setSpreadsheetData,
      () => {
        setSaving(false)
        setSnackbarMessage({ status: 'success', message: 'Lookups saved' })
        setOpen(false)
      },
      (error) => {
        setSaving(false)
        displayErrorMessage(error)
        // The lookups did save: shown as saved, so another Save doesn't send them again.
        loadLookups().catch(() => undefined)
      }
    )
  }, [viewSavePending])

  // ——— The open source ————————————————————————————————————————————————————————————————————

  // The words between a rule's parts, quiet: "and", "matches".
  const ruleWord = (word: string): React.ReactNode => (
    <span className="flex shrink-0 items-center px-1.5 text-[13px] text-muted select-none">{word}</span>
  )

  // The source table as its blue icon alone, its name on hover, and a chevron on to its column: "⊞ ›".
  const tableOwnerSegment = (name: string): React.ReactNode => (
    <span className="flex shrink-0 items-center gap-1 pl-1.5 select-none [&_svg]:size-3.5 [&_svg]:shrink-0">
      <Tooltip content={name}>
        <span className="flex text-blue" aria-label={name}>
          <Grid />
        </span>
      </Tooltip>
      <span className="flex text-faint">
        <Triangle type="right" />
      </span>
    </span>
  )

  const ownColumnOptions = () =>
    ownColumns.map((column) => ({
      label: column.name,
      value: column.publicId,
      icon: <IconSelector kind={column.kind} />
    }))

  // A rule, as Edit column's dependencies are written: the source's column, "matches", then this table's.
  const matchRule = (draft: LookupDraft, rule: IMapJoinColumn, index: number): React.ReactNode => {
    const target = table.columns.find((column) => column.publicId === rule.targetColumnId)
    const sourceColumns = sourceViews[draft.viewId]?.columns
    const implied = linkedSourceColumn(target, draft.viewId)
    const impliedColumn = implied && sourceColumns?.find((column) => column.publicId === implied)
    return (
      <PanelRule
        key={`${draft.key}-rule-${index}`}
        id={`${draft.key}-rule-${index}`}
        removeLabel={`Remove the dependency on ${target?.name ?? 'this column'}`}
        onRemove={() => removeRule(draft, index)}
        disabled={saving}
        data-testid="lookup-rule"
      >
        {index > 0 && ruleWord('and')}
        {tableOwnerSegment(draft.tableName)}
        {impliedColumn ? (
          <span className={STATIC_FIELD_SEGMENT}>
            <IconSelector kind={impliedColumn.kind} />
            <span className="min-w-0 truncate">{impliedColumn.name}</span>
          </span>
        ) : (
          <PanelSegmentSelect
            tone="field"
            ariaLabel={`Column in ${draft.tableName}`}
            placeholder="Choose a column"
            options={(sourceColumns ?? [])
              .filter(
                (column) => !column.isJoined && !column.isSynced && !BLOCK_SELECT_OPTION_TYPES.includes(column.kind)
              )
              .map((column) => ({
                label: column.name,
                value: column.publicId,
                icon: <IconSelector kind={column.kind} />
              }))}
            selected={rule.sourceColumnId ? [rule.sourceColumnId] : []}
            onPick={(sourceColumnId) => setRuleSource(draft, index, sourceColumnId)}
            loading={sourceColumns === undefined}
            defaultOpen={!rule.sourceColumnId}
            disabled={saving}
            className="max-w-[160px] shrink-0 pl-1 font-medium"
          />
        )}
        <PanelSegmentSelect
          lead="matches"
          tone="field"
          ariaLabel={`Column in ${table.name}`}
          options={ownColumnOptions()}
          selected={rule.targetColumnId ? [rule.targetColumnId] : []}
          onPick={(targetColumnId) => setRuleTarget(draft, index, targetColumnId)}
          disabled={saving}
          className="max-w-[160px] shrink-0 font-medium"
        />
      </PanelRule>
    )
  }

  // A column brought in, as the settings list it: its icon and name (and the name it was given here, if
  // another), without a switch; those are in the Edit columns dialog.
  const importedRow = (draft: LookupDraft, column: ITableViewColumn): React.ReactNode => {
    const join = draft.joinId && table.joins?.find((item) => item.publicId === draft.joinId)
    const targetId = join && join.dataColumns.find((item) => item.sourceColumnId === column.publicId)?.targetColumnId
    const targetName = targetId && table.columns.find((item) => item.publicId === targetId)?.name
    return (
      <div
        key={column.publicId}
        className="flex h-8 items-center gap-2 px-3 text-[13px] font-medium text-body [&_svg]:size-3.5 [&_svg]:shrink-0"
        data-testid="lookup-imported-column"
      >
        <IconSelector kind={column.kind} />
        <span className="min-w-0 truncate">{column.name}</span>
        {targetName && targetName !== column.name && (
          <span className="ml-auto shrink-0 font-normal text-muted">as {targetName}</span>
        )}
      </div>
    )
  }

  // A dependency set by the other table's lookup: the same sentence as a rule, its columns fixed, and no ×.
  const fixedRule = (draft: LookupDraft, rule: IMapJoinColumn, index: number): React.ReactNode => {
    const source = sourceViews[draft.viewId]?.columns.find((column) => column.publicId === rule.sourceColumnId)
    const target = table.columns.find((column) => column.publicId === rule.targetColumnId)
    return (
      <div key={`${draft.key}-rule-${index}`} className="flex max-w-full" data-testid="lookup-rule">
        <PanelPill>
          {index > 0 && ruleWord('and')}
          {tableOwnerSegment(draft.tableName)}
          <span className={STATIC_FIELD_SEGMENT}>
            {source && <IconSelector kind={source.kind} />}
            <span className="min-w-0 truncate">{source?.name ?? rule.sourceColumnName}</span>
          </span>
          {ruleWord('matches')}
          <span className={`${STATIC_FIELD_SEGMENT} pr-2.5`}>
            {target && <IconSelector kind={target.kind} />}
            <span className="min-w-0 truncate">{target?.name}</span>
          </span>
        </PanelPill>
      </div>
    )
  }

  // A column of the source, with its switch. Looked up already under another name (a column here had its
  // name), it says which.
  const columnRow = (draft: LookupDraft, column: ITableViewColumn): React.ReactNode => {
    const on = draft.dataColumns.includes(column.publicId)
    const join = draft.joinId && table.joins?.find((item) => item.publicId === draft.joinId)
    const targetId = join && join.dataColumns.find((item) => item.sourceColumnId === column.publicId)?.targetColumnId
    const targetName = targetId && table.columns.find((item) => item.publicId === targetId)?.name
    return (
      <MenuItem
        key={column.publicId}
        icon={<IconSelector kind={column.kind} />}
        onClick={() => toggleColumn(draft, column.publicId)}
        disabled={saving}
        className="[&_svg]:size-3.5"
        data-testid="lookup-column"
        end={
          <span className="flex items-center gap-3" onClick={(event) => event.stopPropagation()}>
            {on && targetName && targetName !== column.name && (
              <span className="font-normal text-muted">as {targetName}</span>
            )}
            <Toggle
              aria-label={`Look up ${column.name}`}
              toggled={on}
              onToggle={() => toggleColumn(draft, column.publicId)}
              disabled={saving}
            />
          </span>
        }
      >
        <span className="min-w-0 truncate">{column.name}</span>
      </MenuItem>
    )
  }

  // This table's rows, one line per set of values they're matched on (in the table's order), with how many rows
  // have them and the source's rows that match them: what those rows take. Several matching source rows give
  // the first (in the source view's order, as the lookup does), or all of their values as a list. Lines with a
  // match come first.
  const matchedGroups = (draft: LookupDraft) => {
    const sourceView = sourceViews[draft.viewId]
    const preview = previews[draft.viewId]
    if (!sourceView || !preview || !ownRows) return undefined
    const columnOf = (id: string) => sourceView.columns.find((column) => column.publicId === id)
    const rules = draft.joinColumns
      .map((rule) => ({
        source: columnOf(rule.sourceColumnId),
        own: table.columns.find((column) => column.publicId === rule.targetColumnId)
      }))
      .filter((rule): rule is { source: ITableViewColumn; own: ITableColumn } => !!rule.source && !!rule.own)
    const keyColumns = rules.map((rule) => rule.source)
    const ownKeyColumns = rules.map((rule) => rule.own)
    const dataColumns = draft.dataColumns
      .filter((id) => !draft.joinColumns.some((rule) => rule.sourceColumnId === id))
      .map(columnOf)
      .filter((column): column is ITableViewColumn => !!column)
    const comparisons = keyComparisons(rules, takesAll(draft))
    const byValues = new Map<string, { own: string[][]; count: number }>()
    ownRows.rows.forEach((row) => {
      const own = ownKeyColumns.map((column) => matchValues(row[column.name]))
      // By the values themselves, so a value "A, B" and a list of A and B are lines of their own.
      const key = JSON.stringify(own)
      byValues.set(key, { own, count: (byValues.get(key)?.count ?? 0) + 1 })
    })
    const sourceKeys = preview.rows.map((row) => keyColumns.map((column) => matchValues(row[column.name])))
    const lines = [...byValues.values()].map(({ own, count }) => ({
      values: own.map((items) => items.join(', ')),
      count,
      rows: preview.rows.filter((_, index) => keysMatch(sourceKeys[index], own, comparisons))
    }))
    const landed = lines.filter((line) => line.rows.length > 0)
    const unmatched = lines.filter((line) => line.rows.length === 0)
    return {
      keyColumns,
      ownKeyColumns,
      dataColumns,
      lines: [...landed, ...unmatched],
      landed,
      unmatchedRows: unmatched.reduce((sum, line) => sum + line.count, 0),
      preview
    }
  }

  // The other values a line's matching rows give a column, past the first: what taking the first leaves out.
  const otherValues = (rows: Array<Record<string, unknown>>, column: ITableViewColumn): number => {
    const first = previewValue(rows[0][column.name])
    return new Set(
      rows
        .slice(1)
        .map((row) => previewValue(row[column.name]))
        .filter((value) => value !== '' && value !== first)
    ).size
  }

  // The imported columns that several matching source rows give rows here different values in.
  const ambiguousColumns = (matched: NonNullable<ReturnType<typeof matchedGroups>>): ITableViewColumn[] =>
    matched.dataColumns.filter((column) =>
      matched.landed.some(({ rows }) => rows.length > 1 && otherValues(rows, column) > 0)
    )

  // A table connected through a select's options, with no lookup of its own: the select's, not removable here.
  const connectionOf = (draft: LookupDraft): boolean =>
    !draft.joinId && ownColumns.some((column) => linkedSourceColumn(column, draft.viewId))

  const sourcePicked = !!sourcePicker
  const pickerViews = sourcePicker ? tableViews[sourcePicker.tableId] : undefined
  const pickerDraft = sourcePicker && drafts.find((draft) => draft.key === sourcePicker.key)
  // A view with filter variables changes per person, so it can't be a lookup's source; nor can one another
  // lookup here already uses.
  const viewUnavailable = (view: ITableViewWithColumns): string | undefined => {
    if (drafts.some((draft) => draft.viewId === view.publicId && draft.key !== sourcePicker?.key))
      return 'Another lookup here uses this view'
    const variables = view.filterSettings?.some(
      (filter) =>
        FilterVariables.includes(filter.value) ||
        filter.multipleValues?.some((value) => FilterVariables.includes(value))
    )
    return variables ? 'Its filters change per person, so it can’t be a lookup’s source' : undefined
  }

  const openSourcePicker = (draft: LookupDraft): void => {
    setSourcePicker({ key: draft.key, tableId: draft.tableId, viewId: draft.viewId })
    loadTableViews(draft.tableId).catch(() => setTableViews((current) => ({ ...current, [draft.tableId]: null })))
  }

  const pickTable = (tableId: string): void => {
    setSourcePicker((current) => current && { ...current, tableId, viewId: '' })
    loadTableViews(tableId)
      .then((views) => {
        const picked = tables?.find((candidate) => candidate.publicId === tableId)
        const view =
          views.find((candidate) => candidate.publicId === picked?.defaultViewId && !viewUnavailable(candidate)) ??
          views.find((candidate) => !viewUnavailable(candidate))
        if (view) setSourcePicker((current) => current && { ...current, viewId: view.publicId })
      })
      .catch(displayErrorMessage)
  }

  // Another view of the same table keeps the columns and dependencies, which are the table's; another table
  // starts afresh, matched already when a select here takes its options from it.
  const applySourcePicker = (): void => {
    if (!sourcePicker || !pickerDraft) return
    const view = pickerViews?.find((candidate) => candidate.publicId === sourcePicker.viewId)
    if (!view) return
    const sameTable = sourcePicker.tableId === pickerDraft.tableId
    const linked = ownColumns.find((column) => linkedSourceColumn(column, view.publicId))
    updateDraft(pickerDraft.key, (item) => ({
      ...item,
      tableId: sourcePicker.tableId,
      tableName: tables?.find((candidate) => candidate.publicId === sourcePicker.tableId)?.name ?? item.tableName,
      viewId: view.publicId,
      viewName: view.name,
      ...(!sameTable && {
        joinColumns: linked
          ? [{ targetColumnId: linked.publicId, sourceColumnId: linkedSourceColumn(linked, view.publicId)! }]
          : [],
        dataColumns: [],
        isOneToMany: false
      })
    }))
    setSourcePicker(null)
  }

  // A saved lookup is removed on Save (nothing looked up from it); one added here goes at once. A
  // connection through a select can't be removed: it's the select's.
  const removeLookup = (draft: LookupDraft): void => {
    if (draft.joinId) updateDraft(draft.key, (item) => ({ ...item, dataColumns: [] }))
    else setDrafts((current) => current.filter((item) => item.key !== draft.key))
    setSourcePicker(null)
  }

  const settings = (draft: LookupDraft): React.ReactNode => {
    const sourceView = sourceViews[draft.viewId]
    const savedDraft = savedByKey(draft.key)
    // The select this table is connected through, when it's a connection with no lookup of its own yet.
    const connectedBy = connectionOf(draft)
      ? table.columns.find((column) => linkedSourceColumn(column, draft.viewId))
      : undefined
    // Listed because that table looks up from this one: the link is its lookup's, so it isn't changed or removed
    // here, until a column imported from it makes it a lookup of this table's own.
    const linkedByLookup = !!draft.looksUpFromHere && !draft.joinId
    const locked = !!connectedBy || linkedByLookup
    // The source's columns that can be looked up: not one filled by a lookup of its own, nor a synced one.
    // Those looked up when the dialog opened come first, then the rest in the source's order; a switch
    // flipped here leaves its row where it is, under the pointer.
    const columns = (sourceView?.columns ?? []).filter((column) => !column.isJoined && !column.isSynced)
    const savedOn = savedDraft?.dataColumns ?? []
    const ordered = [
      ...columns.filter((column) => savedOn.includes(column.publicId)),
      ...columns.filter((column) => !savedOn.includes(column.publicId))
    ]
    const imported = ordered.filter((column) => draft.dataColumns.includes(column.publicId))
    const removing = !!draft.joinId && draft.dataColumns.length === 0
    const recreating =
      !!savedDraft && (savedDraft.viewId !== draft.viewId || savedDraft.isOneToMany !== takesAll(draft))
    const forced = forcesOneToMany(draft)

    return (
      <div className="flex flex-col gap-5" data-testid="lookup-group">
        {/* The source, as Edit column's Source column shows it: on light blue, its table in blue, a pencil to
            change it in a small dialog of its own. */}
        <Field
          label="Source table"
          htmlFor={locked ? undefined : 'lookup-source-button'}
          className="flex flex-col gap-1.5"
        >
          {locked ? (
            // A connection set elsewhere (by a select's options, or another table's lookup) is changed there, so
            // the source isn't a button: a lock says so, and the arrow beside it opens the table in a new tab.
            <div className={`${SOURCE_ROW} pr-1`} data-testid="lookup-source-row">
              {sourceName(draft)}
              <Tooltip
                content={
                  connectedBy ? `Set by the options of ${connectedBy.name}` : `Set by a lookup in ${draft.tableName}`
                }
              >
                <span className="ml-auto flex text-blue/60" data-testid="lookup-source-locked">
                  <Lock />
                </span>
              </Tooltip>
              <Tooltip content={`Open ${draft.tableName} in a new tab`}>
                <Link
                  to={generatePath(TABLE, {
                    projectId: project.publicId,
                    tableId: draft.tableId,
                    viewId: draft.viewId
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${draft.tableName} in a new tab`}
                  className="flex size-7 shrink-0 items-center justify-center rounded-md text-blue hover:bg-blue/10"
                  data-testid="lookup-source-open"
                >
                  <ExternalLink />
                </Link>
              </Tooltip>
            </div>
          ) : (
            <Button
              id="lookup-source-button"
              internalType="transparent"
              className={`${SOURCE_ROW} hover:bg-really-light-blue hover:text-body hover:brightness-[0.98]`}
              aria-haspopup="dialog"
              onClick={() => openSourcePicker(draft)}
              disabled={saving}
            >
              {sourceName(draft)}
              <span className="ml-auto flex text-blue">
                <Pencil />
              </span>
            </Button>
          )}
          {connectedBy && (
            <span className="text-muted" data-testid="lookup-connected-by">
              Connected through the options of <ColumnName column={connectedBy} />.
            </span>
          )}
          {linkedByLookup && (
            <span className="text-muted" data-testid="lookup-connected-by">
              Connected through a lookup in <TableName name={draft.tableName} />.
            </span>
          )}
        </Field>

        {sourceView === null ? (
          <p className="text-muted">
            <TableName name={draft.tableName} /> can&apos;t be opened with your access, so its lookup can&apos;t be
            changed here.
          </p>
        ) : (
          <>
            {/* The columns brought in, a few at a time: all the source's are in a dialog of their own, with a
                search, as a source can have hundreds. */}
            <section className="flex flex-col border-t border-divider pt-4">
              <div className="mb-1.5 flex h-6 items-center justify-between">
                <span className="text-[13px] leading-5 font-medium text-body">Imported columns</span>
                {imported.length > 0 && (
                  <Tooltip content="Edit columns">
                    <Button
                      internalType="transparent"
                      aria-label="Edit columns"
                      className="-mr-1 size-6 shrink-0 rounded-md p-0 text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-3.5"
                      onClick={() => openColumns()}
                      disabled={saving || sourceView === undefined}
                    >
                      <Pencil />
                    </Button>
                  </Tooltip>
                )}
              </div>
              {sourceView !== undefined && imported.length === 0 ? (
                // On first use, the way in, set as Add dependency is below.
                <div>
                  <Button
                    internalType="outline"
                    className="h-8 gap-1.5 px-2.5 text-[13px] font-medium [&_svg]:size-4 [&_svg]:text-muted"
                    onClick={() => openColumns()}
                    disabled={saving}
                    data-testid="lookup-import-columns"
                  >
                    <Plus />
                    Import columns
                  </Button>
                </div>
              ) : (
                /* One bordered list, as a setting's box is, its rows on the source button's inset. */
                <div
                  className="flex flex-col overflow-hidden rounded-lg border border-divider py-1"
                  data-testid="lookup-imported-list"
                >
                  {sourceView === undefined ? (
                    <div className="flex h-8 items-center px-3">
                      <Spinner className="size-4" />
                    </div>
                  ) : (
                    imported.slice(0, LISTED_IMPORTED).map((column) => importedRow(draft, column))
                  )}
                  {imported.length > LISTED_IMPORTED && (
                    <Button
                      internalType="transparent"
                      className="-mb-1 mt-1 h-8 w-full justify-start rounded-none border-t border-divider px-3 text-[13px] font-normal text-muted hover:bg-surface-hover hover:text-body"
                      onClick={() => openColumns()}
                      data-testid="lookup-view-all-columns"
                    >
                      View all {imported.length} imported columns
                    </Button>
                  )}
                </div>
              )}
            </section>

            {/* The dependencies that pick each row's row in the source, set out as Edit column's are. */}
            <section className="flex flex-col border-t border-divider pt-4">
              <span className="mb-1.5 flex items-center gap-1.5 text-[13px] leading-5 font-medium text-body">
                <span className="flex text-blue [&_svg]:size-4">
                  <Dependency />
                </span>
                Dependencies
              </span>
              <span className="text-muted">
                Each row takes its values from the{' '}
                {/* The table as its blue icon alone, its name on hover: the source above names it. */}
                <Tooltip content={draft.tableName}>
                  <span
                    className="mx-0.5 inline-flex align-[-2px] text-blue [&_svg]:size-3.5"
                    aria-label={draft.tableName}
                  >
                    <Grid />
                  </span>
                </Tooltip>{' '}
                row where:
              </span>
              <div className="-mx-3">
                {draft.joinColumns.length > 0 && (
                  <PanelRules ids={draft.joinColumns.map((_, index) => `${draft.key}-rule-${index}`)}>
                    {draft.joinColumns.map((rule, index) =>
                      locked ? fixedRule(draft, rule, index) : matchRule(draft, rule, index)
                    )}
                  </PanelRules>
                )}
                {/* Set elsewhere (a select's options, or the other table's lookup): shown, not changed or added
                    to, until a column imported from it is saved as this table's own lookup. */}
                {locked ? (
                  <div className="pb-3" />
                ) : (
                  <PanelAdd
                    label="Add dependency"
                    options={ownColumnOptions().filter(
                      (option) => !draft.joinColumns.some((rule) => rule.targetColumnId === option.value)
                    )}
                    onPick={(targetColumnId) => addRule(draft, targetColumnId)}
                    disabled={saving}
                    noOptionsMessage="No other columns can be matched."
                  />
                )}
              </div>
              {/* When the source has rows sharing a value, what a row takes then: the first, or them all. */}
              {/* In a box of its own, as Edit column's Auto-select is under its dependencies: what it is and does
                  on the left, the choice at the top right. */}
              {/* Side by side when there's room, the choice under the sentence when there isn't. */}
              <div className="@container rounded-lg border border-divider p-3">
                <div className="flex flex-col gap-2.5 @min-[26rem]:flex-row @min-[26rem]:items-start @min-[26rem]:gap-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-1 whitespace-normal">
                    <label htmlFor="lookup-several" className="text-[13px] leading-5 font-medium text-body">
                      When several rows match
                    </label>
                    <span className="text-[13px] leading-5 text-muted">
                      {forced ? (
                        <>
                          A dependency is on a list in <TableName name={draft.tableName} />, so each row takes them all.
                        </>
                      ) : draft.isOneToMany ? (
                        'Each row takes the values of every matching row, as a list.'
                      ) : (
                        'Each row takes the first matching row in the source view’s order.'
                      )}
                    </span>
                  </div>
                  <div className="w-full shrink-0 @min-[26rem]:w-[156px]">
                    <Select
                      inputId="lookup-several"
                      size="sm"
                      searchable={false}
                      options={[
                        { label: 'Take first', value: 'first' },
                        { label: 'Take all as list', value: 'all' }
                      ]}
                      optionsSelected={[takesAll(draft) ? 'all' : 'first']}
                      onOptionClick={(value) =>
                        updateDraft(draft.key, (item) => ({ ...item, isOneToMany: value === 'all' }))
                      }
                      disabled={saving || forced}
                    />
                  </div>
                </div>
              </div>
            </section>

            {removing && (
              <p className="text-muted" data-testid="lookup-removing">
                Nothing is looked up from <TableName name={draft.tableName} />, so saving removes this lookup and its
                columns.
              </p>
            )}
            {recreating && !removing && (
              <p className="text-muted">
                Saving recreates the columns looked up from <TableName name={draft.tableName} />, so formulas that use
                them may need updating.
              </p>
            )}
          </>
        )}
      </div>
    )
  }

  // ——— The sidebar ——————————————————————————————————————————————————————————————————————————

  const lookupTableOptions = (tables ?? [])
    .filter((candidate) => candidate.publicId !== table.publicId)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((candidate) => ({ label: candidate.name, value: candidate.publicId, icon: <Spreadsheet /> }))

  const openTablePicker = (event: React.SyntheticEvent<HTMLElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect()
    setTablePicker({ open: true, top: `${rect.bottom + 4}px`, left: `${rect.left}px`, right: 'auto', bottom: 'auto' })
  }

  // Each table looked up from, or connected through a select's options.
  const sidebar = (
    <>
      {/* Headed as a menu's section is, with a + to link another table that stays in reach however long the
          list grows. */}
      {/* Pulled up by its label's top padding, so its text starts level with the settings' first label. */}
      <div className="-mt-1.5 flex h-7 items-center justify-between">
        <MenuLabel>Source tables</MenuLabel>
        <Tooltip content="Add source table">
          <Button
            internalType="transparent"
            aria-label="Add source table"
            className="size-6 shrink-0 rounded-md p-0 text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-3.5"
            onClick={openTablePicker}
            disabled={saving || !tables}
            data-testid="lookup-add-table-header"
          >
            <Plus />
          </Button>
        </Tooltip>
      </div>
      {drafts.map((draft) => {
        const active = activeDraft?.key === draft.key
        return (
          <MenuItem
            key={draft.key}
            icon={
              <span className="flex text-blue [&_svg]:size-4">
                <Grid />
              </span>
            }
            onClick={() => setActiveKey(draft.key)}
            className={active ? 'bg-really-light-blue text-blue hover:bg-really-light-blue' : undefined}
            data-testid="lookup-source"
          >
            <Tooltip content={draft.tableName} showOnlyIfTruncated>
              <span className="min-w-0 truncate" aria-current={active || undefined}>
                {draft.tableName}
              </span>
            </Tooltip>
          </MenuItem>
        )
      })}
      <MenuItem
        icon={<Plus />}
        className="font-normal!"
        onClick={openTablePicker}
        disabled={saving || !tables}
        data-testid="lookup-add-table"
      >
        Add source table
      </MenuItem>
    </>
  )

  // ——— The preview ——————————————————————————————————————————————————————————————————————————

  const previewEmpty = (title: string, description: string): React.ReactNode => (
    <div className="px-3 pt-2 pb-3">
      <Empty icon={<Grid />} title={title} description={description} />
    </div>
  )

  // Each imported column under the name it has in this table, which may differ from the source's.
  const nameHere = (draft: LookupDraft, column: ITableViewColumn): string => {
    const join = draft.joinId && table.joins?.find((item) => item.publicId === draft.joinId)
    const targetId = join && join.dataColumns.find((item) => item.sourceColumnId === column.publicId)?.targetColumnId
    return (targetId && table.columns.find((item) => item.publicId === targetId)?.name) || column.name
  }

  // The table of what this table's rows take: the values its rows are matched on, a line per set of them (those
  // with a match first), and what those rows take from the source, or No match. Headed by both tables' columns,
  // the source's first, each row of headings after its table's icon.
  // Wider cells when expanded. Room at its foot for the notices that float over it.
  const previewTable = (expanded: boolean): React.ReactNode => {
    if (!activeDraft) return null
    const sourceView = sourceViews[activeDraft.viewId]
    const preview = previews[activeDraft.viewId]
    if (sourceView === null || preview === null)
      return previewEmpty(
        "Source can't be opened",
        "Its rows aren't shown, as the table or view can't be opened with your access."
      )
    if (ownRows === null)
      return previewEmpty(
        "This table's rows can't be read",
        "What its rows take isn't shown, as they can't be read with your access."
      )
    const matched = matchedGroups(activeDraft)
    if (!matched)
      return (
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="size-5" />
        </div>
      )
    if (matched.keyColumns.length === 0 || matched.dataColumns.length === 0)
      return previewEmpty('Nothing looked up yet', 'Import a column and add a dependency, and its values show here.')
    if (matched.lines.length === 0)
      return previewEmpty(
        'No rows yet',
        `${table.name} has no rows to look values up for. Rows added here are looked up.`
      )
    const valueOf = (rows: Array<Record<string, unknown>>, column: ITableViewColumn): string =>
      takesAll(activeDraft)
        ? [...new Set(rows.map((row) => previewValue(row[column.name])).filter(Boolean))].join(', ')
        : previewValue(rows[0][column.name])
    const search = previewSearch.trim().toLowerCase()
    const lines = search
      ? matched.lines.filter(
          ({ values, rows }) =>
            values.some((value) => value.toLowerCase().includes(search)) ||
            (rows.length > 0 &&
              matched.dataColumns.some((column) => valueOf(rows, column).toLowerCase().includes(search)))
        )
      : matched.lines
    // Why a line has no match: a value it's matched on is empty, which matches nothing, or no source row has its
    // values.
    const noMatchReason = (values: string[]): string => {
      const empty = matched.ownKeyColumns.find((_, index) => values[index] === '')
      return empty
        ? `${empty.name} is empty in these rows, and an empty value matches nothing.`
        : `No row in ${activeDraft.tableName} has ${values.join(' and ')}.`
    }
    const cellWidth = expanded ? 'max-w-[320px]' : 'max-w-[200px]'
    // Every column ruled off from the next; the values brought in on white, apart from those matched on.
    const cell = `h-8 ${cellWidth} truncate border-r border-b border-divider px-3 whitespace-nowrap`
    const output = 'bg-surface-raised'
    // The headings in their medium weight, ruled off like the cells.
    const headingCell = 'border-r border-b border-divider px-3 text-left font-medium whitespace-nowrap'
    // Two bands of headings, 12px above and below each line: the source's on the light blue a source has
    // everywhere, then this table's on white.
    const firstRow = 'border-b-0 bg-really-light-blue py-3'
    const secondRow = 'bg-surface-raised py-3'
    // A column's heading. A source column's carries, in a small pill on the seam between the two bands, centred
    // under its type icon, how it reaches this table's column below: the dependency's icon, for one the rows are matched on,
    // or an arrow down, for one whose values are brought in.
    const header = (
      key: string,
      kind: ITableViewColumn['kind'],
      name: string,
      source: boolean,
      mark?: { icon: React.ReactNode; label: string }
    ) => (
      <th key={key} className={`${headingCell} ${source ? `${firstRow} relative` : secondRow} text-body`}>
        <span className="flex items-center gap-1 [&_svg]:size-3.5 [&_svg]:shrink-0">
          <span className="flex text-muted">
            <IconSelector kind={kind} />
          </span>
          <span className={`${expanded ? 'max-w-[280px]' : 'max-w-[160px]'} truncate`}>{name}</span>
        </span>
        {mark && (
          <Tooltip content={mark.label}>
            <span
              className="absolute bottom-0 left-[7px] z-[2] flex h-4 w-6 translate-y-1/2 items-center justify-center rounded-full border border-divider bg-surface-raised text-blue [&_svg]:size-3"
              aria-label={mark.label}
              data-testid="lookup-preview-column-mark"
            >
              {mark.icon}
            </span>
          </Tooltip>
        )}
      </th>
    )
    // At the start of each heading row, its table: the source's icon in blue, then this one's, which each is and
    // its name on hover.
    const tableMark = (name: string, current: boolean) => (
      <th className={`${headingCell} ${current ? secondRow : firstRow} w-0 px-2`}>
        <Tooltip content={`${current ? 'This table' : 'Source table'}: ${name}`}>
          <span
            className={`flex [&_svg]:size-3.5 ${current ? 'text-body' : 'text-blue'}`}
            aria-label={`${current ? 'This table' : 'Source table'}: ${name}`}
            data-testid="lookup-preview-table-mark"
          >
            <Grid />
          </span>
        </Tooltip>
      </th>
    )
    return (
      // Room under the last line for the notice.
      <div className="min-h-0 flex-1 overflow-auto pb-12">
        {/* The columns as wide as what's in them, a last empty one taking the rest of the width. */}
        <table className="w-full border-separate border-spacing-0 text-[13px]" data-testid="lookup-preview">
          {/* Two rows of headings, one per table: the source's columns, where values come from, then this table's,
              where they go. A matched value is the same in both, so it sits under both names. */}
          <thead className="sticky top-0 z-[1]">
            <tr data-testid="lookup-preview-source-columns">
              {tableMark(activeDraft.tableName, false)}
              {matched.keyColumns.map((column, index) =>
                header(`source-${column.publicId}`, column.kind, column.name, true, {
                  icon: <Dependency />,
                  label: `${matched.ownKeyColumns[index].name} matches ${column.name}`
                })
              )}
              {matched.dataColumns.map((column) =>
                header(`source-${column.publicId}`, column.kind, column.name, true, {
                  icon: <Arrow type="down" />,
                  label: `${nameHere(activeDraft, column)} takes ${column.name}`
                })
              )}
              <th aria-hidden className={`${headingCell} ${firstRow} w-full border-r-0`} />
            </tr>
            <tr data-testid="lookup-preview-own-columns">
              {tableMark(table.name, true)}
              {matched.ownKeyColumns.map((column) => header(column.publicId, column.kind, column.name, false))}
              {matched.dataColumns.map((column) =>
                header(column.publicId, column.kind, nameHere(activeDraft, column), false)
              )}
              <th aria-hidden className={`${headingCell} ${secondRow} w-full border-r-0`} />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr>
                <td
                  colSpan={matched.ownKeyColumns.length + matched.dataColumns.length + 2}
                  className="h-8 px-3 whitespace-nowrap text-muted"
                >
                  No values match “{previewSearch.trim()}”.
                </td>
              </tr>
            )}
            {lines.map(({ values, count, rows }, index) => (
              <tr key={index} data-testid="lookup-preview-row">
                {/* Under the tables' marks. */}
                <td aria-hidden className="border-r border-b border-divider" />
                {values.map((value, position) => (
                  <td key={matched.ownKeyColumns[position].publicId} className={`${cell} text-muted`}>
                    {value || <span className="text-faint">Empty</span>}
                    {/* After the last value, how many rows here have them, when more than one. */}
                    {position === values.length - 1 && count > 1 && (
                      <span className="ml-1.5 text-faint" data-testid="lookup-preview-row-count">
                        · {count.toLocaleString()} rows
                      </span>
                    )}
                  </td>
                ))}
                {/* What these rows take: the first value or the list, Empty when the match has none; or, said
                    once across the imported columns, that nothing matches. */}
                {rows.length > 0 ? (
                  matched.dataColumns.map((column) => {
                    const more = takesAll(activeDraft) ? 0 : otherValues(rows, column)
                    return (
                      <td key={column.publicId} className={`${cell} ${output} text-body`}>
                        {valueOf(rows, column) || <span className="text-faint">Empty</span>}
                        {/* After a value, the other values its matching rows have, which taking the first leaves out. */}
                        {more > 0 && (
                          <Tooltip
                            content={`${more} other ${more === 1 ? 'value' : 'values'} in matching rows; each row takes the first.`}
                          >
                            <span className="ml-1.5 text-muted" data-testid="lookup-preview-more">
                              +{more} more
                            </span>
                          </Tooltip>
                        )}
                      </td>
                    )
                  })
                ) : (
                  <td colSpan={matched.dataColumns.length} className={`${cell} ${output} text-faint`}>
                    <Tooltip content={noMatchReason(values)}>
                      <span data-testid="lookup-preview-no-match">No match</span>
                    </Tooltip>
                  </td>
                )}
                <td aria-hidden className="w-full border-b border-divider" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const activeMatched = activeDraft && matchedGroups(activeDraft)
  const previewShown = !!activeMatched && activeMatched.keyColumns.length > 0 && activeMatched.dataColumns.length > 0
  const activeAmbiguous = activeMatched && previewShown ? ambiguousColumns(activeMatched) : []
  // This table's rows, and how many a value lands on; the source's, when only its first are read.
  const previewCount = (): string | undefined => {
    if (!activeMatched || !previewShown || !ownRows) return undefined
    const { unmatchedRows, preview } = activeMatched
    const read = ownRows.rows.length
    const rows =
      ownRows.total > read
        ? `${(read - unmatchedRows).toLocaleString()} of the first ${read.toLocaleString()} rows matched`
        : `${(read - unmatchedRows).toLocaleString()} of ${read.toLocaleString()} ${read === 1 ? 'row' : 'rows'} matched`
    return preview.total > preview.rows.length
      ? `${rows} · first ${preview.rows.length.toLocaleString()} of ${preview.total.toLocaleString()} source rows`
      : rows
  }

  // A notice over the foot of the table: in orange, what to look at; in green, that all is well.
  const notice = (key: string, warn: boolean, text: string, card?: React.ReactNode): React.ReactNode => (
    <div
      key={key}
      className="pointer-events-auto flex min-h-8 max-w-full min-w-0 items-start gap-2 self-start rounded-lg border border-divider bg-surface-raised px-2.5 py-1.5 text-[13px] leading-5 whitespace-normal text-body shadow-[0_4px_12px_-2px_rgb(0_0_0/0.12),0_2px_4px_-2px_rgb(0_0_0/0.08)] [&_svg]:size-3.5 [&_svg]:shrink-0"
      data-testid="lookup-preview-notice"
      data-state={warn ? 'warning' : 'ok'}
    >
      <span className={`flex h-5 items-center ${warn ? 'text-orange' : 'text-dark-green'}`}>
        {warn ? <Warning /> : <Tick />}
      </span>
      <span className="min-w-0">
        {card ? (
          <HoverCard plain title={text} content={card}>
            {text}
          </HoverCard>
        ) : (
          text
        )}
      </span>
    </div>
  )

  // Over the foot of the table, about the rows with a match: in orange, the columns where one takes the first of
  // several values; in green, when none does, that every match is a single row, or that matching rows agree, or
  // that every value is taken, as a list. A row with no match isn't a problem (its line says No match, and the
  // footer counts them), so there's no note when nothing matches.
  const previewNotice = (): React.ReactNode => {
    if (!activeDraft || !activeMatched || !previewShown || activeMatched.landed.length === 0) return null
    const count = activeAmbiguous.length
    const ambiguous = count > 0 && !takesAll(activeDraft)
    // Several source rows matching one row, whether or not they agree on the imported values.
    const multiple = activeMatched.landed.some(({ rows }) => rows.length > 1)
    const okText = (): string => {
      if (count > 0) return 'Matches found in source table for dependencies'
      if (multiple) return 'Matches found in source table, with the same values in each'
      return activeMatched.unmatchedRows > 0
        ? 'One match found in source table for each matched row'
        : 'One match found in source table for each row'
    }
    return (
      <div className="pointer-events-none absolute inset-x-2 bottom-2 z-[2] flex flex-col gap-1.5">
        {ambiguous
          ? notice(
              'ambiguous',
              true,
              `Multiple matches found in source table, first value used in ${count} ${count === 1 ? 'column' : 'columns'}`,
              <span className="flex flex-col gap-1.5">
                <span>
                  Several rows in <TableName name={activeDraft.tableName} /> match the same value with different values
                  in {count === 1 ? 'this column' : 'these columns'}, so each row takes the first, in the source
                  view&apos;s order. Change this under When several rows match.
                </span>
                <span className="flex flex-wrap gap-x-3 gap-y-1">
                  {activeAmbiguous.map((column) => (
                    <ColumnName
                      key={column.publicId}
                      column={{ kind: column.kind, name: nameHere(activeDraft, column) }}
                    />
                  ))}
                </span>
              </span>
            )
          : notice('ok', false, okText())}
      </div>
    )
  }

  const previewFooter = activeDraft && (
    <div className={DIALOG_ASIDE_FOOTER}>
      <span className="min-w-0 truncate pl-1.5 text-[13px] text-muted" data-testid="lookup-preview-count">
        {previewCount()}
      </span>
      <Tooltip content={`Open ${activeDraft.tableName}`}>
        <Link
          to={generatePath(TABLE, {
            projectId: project.publicId,
            tableId: activeDraft.tableId,
            viewId: activeDraft.viewId
          })}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-3.5"
        >
          Source table
          <ExternalLink />
        </Link>
      </Tooltip>
    </div>
  )

  // Under the preview's header, a line over it; in the expanded preview, on its own above the table.
  const previewSearchRow = (expanded: boolean) => (
    <div
      // Expanded, the field runs edge to edge over the table's box, its corners as round (MenuSearch insets
      // itself 4px, taken back here).
      className={`shrink-0 ${expanded ? '-mx-1 -mt-1 [&_label]:rounded-lg' : 'border-t border-divider px-2 pt-1 pb-2'} [&_label]:border [&_label]:border-divider [&_label]:bg-surface-raised`}
    >
      <MenuSearch
        ref={previewSearchRef}
        aria-label="Search the column values"
        placeholder="Search values"
        value={previewSearch}
        onChange={(event) => setPreviewSearch(event.target.value)}
      />
    </div>
  )

  // An icon in the preview's header, as Edit column's preview has its search.
  const previewHeaderButton = (label: string, icon: React.ReactNode, onClick: () => void) => (
    <Tooltip content={label}>
      <Button
        internalType="transparent"
        aria-label={label}
        className="size-6 shrink-0 rounded-md p-0 text-muted hover:bg-surface-hover hover:text-body [&_svg]:size-3.5"
        onClick={onClick}
      >
        {icon}
      </Button>
    </Tooltip>
  )

  const preview = activeDraft && (
    <>
      <div className="flex h-10 shrink-0 items-center gap-1 px-3 text-[13px] font-medium text-body select-none">
        Preview of column values
        {previewShown && (
          <span className="-mr-1.5 ml-auto flex items-center gap-0.5">
            {previewHeaderButton('Expand the preview', <Expand />, () => setPreviewExpanded(true))}
            {!previewSearchOpen &&
              previewHeaderButton('Search the column values', <Search />, () => setPreviewSearchOpen(true))}
          </span>
        )}
      </div>
      {previewShown && previewSearchOpen && !previewExpanded && previewSearchRow(false)}
      <div className="relative flex min-h-0 flex-1 flex-col border-t border-divider">
        {!previewExpanded && previewTable(false)}
        {!previewExpanded && previewNotice()}
      </div>
      {previewFooter}
    </>
  )

  // The preview at the dialog's full width, for a source with many columns or long values: its search open, its
  // notice and footer as beside the settings.
  const expandedPreview = previewExpanded && activeDraft && (
    <Dialog
      open
      width="wide"
      onOpenChange={(next) => !next && setPreviewExpanded(false)}
      title="Preview of column values"
    >
      {/* The search on its own above the table, a step apart from it. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3" data-testid="lookup-preview-expanded">
        {previewSearchRow(true)}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-divider bg-surface-faint">
          <div className="relative flex min-h-0 flex-1 flex-col">
            {previewTable(true)}
            {previewNotice()}
          </div>
          {previewFooter}
        </div>
      </div>
    </Dialog>
  )

  // ——— The dialog ————————————————————————————————————————————————————————————————————————————

  const tableChangeWarning =
    sourcePicker &&
    pickerDraft &&
    sourcePicker.tableId !== pickerDraft.tableId &&
    (pickerDraft.joinColumns.length > 0 || pickerDraft.dataColumns.length > 0)

  // Every column of the source, searchable, each with its switch: changes go to the lookup at once, saved
  // with the rest.
  const openColumns = (): void => {
    setColumnSearch('')
    setColumnsOpenedWith(activeDraft?.dataColumns ?? [])
    setColumnsOpen(true)
  }
  const dialogColumns = activeDraft
    ? (sourceViews[activeDraft.viewId]?.columns ?? []).filter((column) => !column.isJoined && !column.isSynced)
    : []
  const searchedColumns = columnSearch
    ? dialogColumns.filter((column) => column.name.toLowerCase().includes(columnSearch.toLowerCase()))
    : dialogColumns
  const columnGroups = [
    { label: 'Imported', columns: searchedColumns.filter((column) => columnsOpenedWith.includes(column.publicId)) },
    {
      label: 'Other columns',
      columns: searchedColumns.filter((column) => !columnsOpenedWith.includes(column.publicId))
    }
  ].filter((group) => group.columns.length > 0)
  const importedCount = activeDraft
    ? dialogColumns.filter((column) => activeDraft.dataColumns.includes(column.publicId)).length
    : 0

  const columnsDialog = columnsOpen && activeDraft && (
    <Dialog
      open
      tall
      onOpenChange={(next) => !next && setColumnsOpen(false)}
      title={
        <>
          Columns from <TableName name={activeDraft.tableName} className="ml-0.5 [&_svg]:size-4!" />
        </>
      }
      footer={
        <>
          <span className="mr-auto text-[13px] text-muted" data-testid="lookup-columns-count">
            {columnSearch
              ? `${searchedColumns.length} of ${columnCount(dialogColumns.length)}`
              : `${importedCount} of ${columnCount(dialogColumns.length)} imported`}
          </span>
          <Button internalType="outline" className={BLUE_BUTTON} onClick={() => setColumnsOpen(false)}>
            Done
          </Button>
        </>
      }
    >
      {/* The search stays in view as the list scrolls under it. */}
      <div className="sticky -top-4 z-[1] -mt-4 bg-surface-raised pt-4 pb-2">
        {/* Its fill to the rows' hover fill, its icon on the line of theirs. */}
        <div className="-mx-3">
          <MenuSearch
            aria-label={`Search the columns of ${activeDraft.tableName}`}
            placeholder="Search columns"
            value={columnSearch}
            onChange={(event) => setColumnSearch(event.target.value)}
          />
        </div>
      </div>
      <div className="-mx-2 flex flex-col" data-testid="lookup-columns-dialog">
        {searchedColumns.length === 0 ? (
          <span className="px-2 py-1.5 text-muted">No columns match “{columnSearch}”.</span>
        ) : (
          columnGroups.map((group) => (
            <React.Fragment key={group.label}>
              {/* Headed only when there are both, set as a menu's sections are. */}
              {columnGroups.length > 1 && (
                <div className="mt-1 first:mt-0">
                  <MenuLabel>{group.label}</MenuLabel>
                </div>
              )}
              {group.columns.map((column) => columnRow(activeDraft, column))}
            </React.Fragment>
          ))
        )}
      </div>
    </Dialog>
  )

  const sourcePickerDialog = sourcePicker && pickerDraft && (
    <Dialog
      open={sourcePicked}
      onOpenChange={(next) => !next && setSourcePicker(null)}
      title="Source table"
      description={
        <>
          Select the table and view to look up columns from into <TableName name={table.name} current />.
        </>
      }
      footer={
        <>
          {/* Removing, set apart from Save, as Edit column's Remove link is. */}
          {(pickerDraft.joinId ? pickerDraft.dataColumns.length > 0 : !connectionOf(pickerDraft)) && (
            <div className="mr-auto -ml-2">
              <PanelAction destructive onClick={() => removeLookup(pickerDraft)} disabled={saving}>
                Remove lookup
              </PanelAction>
            </div>
          )}
          <Button internalType="outline" onClick={() => setSourcePicker(null)}>
            Cancel
          </Button>
          <Button
            internalType="outline"
            className={BLUE_BUTTON}
            disabled={
              !sourcePicker.viewId ||
              (sourcePicker.tableId === pickerDraft.tableId && sourcePicker.viewId === pickerDraft.viewId)
            }
            onClick={applySourcePicker}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Table" htmlFor="lookup-source-table" className="flex min-w-0 flex-col gap-1.5">
          <Select
            inputId="lookup-source-table"
            options={lookupTableOptions}
            optionsSelected={[sourcePicker.tableId]}
            onOptionClick={pickTable}
            loading={!tables}
          />
        </Field>
        <Field label="View" htmlFor="lookup-source-view" className="flex min-w-0 flex-col gap-1.5">
          <Select
            inputId="lookup-source-view"
            options={(pickerViews ?? []).map((view) => {
              const reason = view.publicId === pickerDraft.viewId ? undefined : viewUnavailable(view)
              return { label: view.name, value: view.publicId, disabled: !!reason, tooltip: reason }
            })}
            optionsSelected={sourcePicker.viewId ? [sourcePicker.viewId] : []}
            onOptionClick={(viewId) => setSourcePicker((current) => current && { ...current, viewId })}
            loading={pickerViews === undefined}
          />
        </Field>
        {tableChangeWarning && (
          <p className="text-muted">
            Another table removes the columns and dependencies set up on <TableName name={pickerDraft.tableName} />.
          </p>
        )}
      </div>
    </Dialog>
  )

  const body = (): React.ReactNode => {
    if (!saved)
      return (
        <div className="flex h-full items-center justify-center">
          <Spinner className="size-5" />
        </div>
      )
    if (!activeDraft)
      return (
        <Empty
          className="h-full w-full"
          icon={<Join />}
          title="No lookups yet"
          description={
            <>
              Bring columns from another table into <TableName name={table.name} current />, matched on a column both
              tables share.
            </>
          }
        >
          <Button
            internalType="outline"
            className="h-8 gap-1.5 px-2.5 text-[13px] font-medium [&_svg]:size-4 [&_svg]:text-muted"
            onClick={openTablePicker}
            disabled={saving || !tables}
          >
            <Plus />
            Add source table
          </Button>
        </Empty>
      )
    return settings(activeDraft)
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : close())}
        title={
          <>
            Lookups in{' '}
            <TableName name={table.name} current className="ml-0.5 [&_svg]:size-4! [&>span:last-child]:font-semibold" />
          </>
        }
        width="wide"
        nav={activeDraft ? sidebar : undefined}
        aside={preview || undefined}
        footer={
          <>
            <span className="mr-auto text-[13px] text-muted" data-testid="lookup-summary">
              {problem ?? changeSummary}
            </span>
            <Button internalType="outline" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button
              internalType="outline"
              className={BLUE_BUTTON}
              disabled={!hasChanges || !!problem}
              isLoading={saving}
              onClick={handleSave}
            >
              Save
            </Button>
          </>
        }
      >
        {body()}
        {/* In the dialog, so they open in its layer and a press on them counts as inside it. */}
        {tablePicker.open && (
          <SelectMenu
            id="lookup-table-picker"
            menuState={tablePicker}
            setMenuState={setTablePicker}
            options={lookupTableOptions}
            optionsSelected={[]}
            onOptionClick={(tableId) => {
              setTablePicker(INITIAL_CONTEXT_MENU_STATE)
              lookUpTable(tableId)
            }}
            width={260}
            searchable
            noOptionsMessage="No other tables in this hub."
          />
        )}
        {sourcePickerDialog}
        {columnsDialog}
        {expandedPreview}
      </Dialog>

      {confirmDiscard && (
        <ActionDialog
          title="Discard your changes?"
          description="Your changes to these lookups haven't been saved."
          confirmLabel="Discard"
          danger
          onClose={() => setConfirmDiscard(false)}
          onConfirm={async () => setOpen(false)}
        />
      )}
    </>
  )
}

/**
 * The table's lookups, opened afresh for another viewer (a masquerade, or another account signing in): what's
 * read depends on what they may see, so nothing read for one is shown to the next.
 */
const TableLookupsDialog: React.FC<TableLookupsDialogProps> = (props) => {
  const who = useViewer()
  return <LookupsDialog key={who} {...props} />
}

export default TableLookupsDialog
